import { TypesenseProductsDocument } from 'shared-code/typesense/typesense.types'
import {
  DSBestListRatingsFacetPriceKey,
  DSImageProps,
  DSProductComparisonChartData,
  DSProductComparisonChartProduct,
  DSProductSearchResults,
} from 'shared-definitions/types'
import { productTagsMapper } from './productTagsMapper'
import { imageMapper } from './imageMapper'
import { PLinkMapperOpts } from './pLinkMapper'
import { wrapWithCurrency } from 'shared-code/format'

interface Opts extends PLinkMapperOpts {
  primaryScore: number
  facetPriceKey: DSBestListRatingsFacetPriceKey

  priceCurrency: string
  strings: {
    price: string
    seeMore: string
  }
}

const PRICE_COLUMN_NAME = 'price'

export function productSearchToProductChart(
  input: DSProductSearchResults,
  opts: Opts
): DSProductComparisonChartData {
  const sortColumns: { label: string; value: string }[] = []
  sortColumns.push({
    label: opts.strings.price,
    value: PRICE_COLUMN_NAME,
  })

  const primeScore = input.products[0]?.primary_scores.find(i => i.id === opts.primaryScore)
  if (primeScore) {
    sortColumns.push({
      label: primeScore.name,
      value: getPrimaryScoreKey(primeScore.id),
    })

    primeScore.core_scores.forEach(coreScore => {
      sortColumns.push({
        label: coreScore.name,
        value: coreScore.code,
      })
    })
  }

  return {
    defaultYSort: PRICE_COLUMN_NAME,
    defaultXSort: sortColumns.length > 1 ? sortColumns[1].value : PRICE_COLUMN_NAME,
    sortColumns,
    products: input.products.map(i => productToChartItemMapper(i, opts)),
  }
}

function productToChartItemMapper(
  product: TypesenseProductsDocument,
  opts: Opts
): DSProductComparisonChartProduct {
  const sortFields: Record<
    string,
    {
      rawValue: number
      displayValue: string
    }
  > = {}

  sortFields[PRICE_COLUMN_NAME] = {
    rawValue: product[opts.facetPriceKey],
    displayValue: wrapWithCurrency(product[opts.facetPriceKey].toString(), opts.priceCurrency),
  }

  const primeScore = product.primary_scores.find(i => i.id === opts.primaryScore)

  if (primeScore) {
    const psValue = primeScore.float_casted_to_int ? primeScore.value / 100 : primeScore.value
    sortFields[getPrimaryScoreKey(primeScore.id)] = {
      rawValue: psValue,
      displayValue: psValue.toFixed(1),
    }

    primeScore.core_scores.forEach(coreScore => {
      const value = coreScore.float_casted_to_int ? coreScore.value / 100 : coreScore.value
      sortFields[coreScore.code] = {
        rawValue: value,
        displayValue: value.toFixed(1),
      }
    })
  }

  return {
    price: null,
    id: product.id,
    title: product.name,
    sortFields,
    tags: productTagsMapper(product),
    subTitle: product.release_date,
    image: product.image ? imageMapper({ src: product.image }, opts) : null,
    badges:
      (product.badges
        .flatMap(badge => badge.image.map(img => imageMapper(img, opts)))
        .filter(img => img !== null) as DSImageProps[]) || [],
    seeMore: opts.strings.seeMore,
  }
}

function getPrimaryScoreKey(id: number): string {
  return `ps_${id}`
}
